/**
 * Owner: Masa Bayu
 * File: measure.ts
 * Date:Time: 2026-10-14 10:38
 * Description: measureLineEdit · bbox preserved · EMPTY/OVERFLOW fail-closed.
 */

import type {
  Font,
  MeasureLineEditResult,
  QXWLine,
} from './types.js';

function tokenize(text: string): readonly string[] {
  const trimmed = text.trim();
  if (trimmed.length === 0) return [];
  return trimmed.split(/\s+/u);
}

function redistribute(
  widths: readonly number[],
  bboxWidth: number,
): { readonly wordStarts: readonly number[]; readonly wordGaps: readonly number[] } {
  const n = widths.length;
  if (n === 0) {
    return { wordStarts: [], wordGaps: [] };
  }
  if (n === 1) {
    return { wordStarts: [0], wordGaps: [] };
  }
  const totalWords = widths.reduce((a, b) => a + b, 0);
  const slack = bboxWidth - totalWords;
  const gapCount = n - 1;
  const gap = slack / gapCount;
  const wordGaps: number[] = [];
  const wordStarts: number[] = [0];
  let x = 0;
  for (let i = 0; i < n; i += 1) {
    if (i > 0) {
      wordGaps.push(gap);
      x += gap;
      wordStarts.push(x);
    }
    x += widths[i]!;
  }
  return { wordStarts, wordGaps };
}

/**
 * Re-measure edited line text within original bbox (A3 / G27).
 * Empty → EMPTY · overflow → OVERFLOW · no newline invent.
 */
export function measureLineEdit(
  line: QXWLine,
  editedText: string,
  fontProvider: (family: string) => Font | null,
): MeasureLineEditResult {
  if (editedText.length === 0 || editedText.trim().length === 0) {
    return { error: 'EMPTY' };
  }

  if (/[\n\r]/.test(editedText)) {
    return { error: 'OVERFLOW' };
  }

  const font = fontProvider(line.typography.family);
  if (font === null) {
    return { error: 'OVERFLOW' };
  }

  const tokens = tokenize(editedText);
  if (tokens.length === 0) {
    return { error: 'EMPTY' };
  }

  const size = line.typography.size;
  const widths = tokens.map((t) => font.measureWidth(t, size));
  const totalWords = widths.reduce((a, b) => a + b, 0);

  if (totalWords > line.layout.width) {
    return { error: 'OVERFLOW' };
  }

  const { wordStarts, wordGaps } = redistribute(widths, line.layout.width);
  const prev = line.linePaint;

  return {
    ...line,
    text: editedText.trim(),
    layout: { ...line.layout },
    linePaint: {
      ...(prev ?? {}),
      wordStarts,
      wordGaps,
      direction: prev?.direction ?? 'ltr',
      script: prev?.script ?? 'latin',
      ...(prev?.role !== undefined ? { role: prev.role } : {}),
      ...(prev?.column !== undefined ? { column: prev.column } : {}),
    },
  };
}
